import { useState, useCallback, useRef } from 'react';
import L from 'leaflet';

/**
 * Starts a one-off polygon draw and invokes onComplete(paths) when done.
 * Also supports editing existing polygons with save/cancel/undo.
 */
export default function useJobsiteDrawing(mapRef) {
  const handlerRef = useRef(null);
  const tempLayerRef = useRef(null);
  const originalPathsRef = useRef(null);
  const onCompleteRef = useRef(null);
  const onCancelRef = useRef(null);
  const [editing, setEditing] = useState(false);

  const startPolygonDraw = useCallback((onComplete) => {
    const map = mapRef.current;
    if (!map) return;

    // Cancel any previous draw/edit
    if (handlerRef.current?.disable) {
      try { handlerRef.current.disable(); } catch (_) {}
      handlerRef.current = null;
    }

    const handler = new L.Draw.Polygon(map, {
      shapeOptions: { color: '#2e7d32', weight: 2, dashArray: '4, 4' },
      allowIntersection: false,
      showArea: false,
    });

    const onCreated = (e) => {
      const layer = e.layer;
      if (layer instanceof L.Polygon) {
        const latlngs = layer.getLatLngs()[0] || [];
        const paths = latlngs.map(ll => ({ lat: ll.lat, lng: ll.lng }));
        try { map.removeLayer(layer); } catch (_) {}
        if (paths.length >= 3 && onComplete) onComplete(paths);
      }
      map.off(L.Draw.Event.CREATED, onCreated);
      handlerRef.current = null;
    };

    map.on(L.Draw.Event.CREATED, onCreated);
    handler.enable();
    handlerRef.current = handler;
  }, [mapRef]);

  /**
   * Edit an existing polygon — renders it as editable on the map.
   * User must use confirmEdit / cancelEdit / undoEdit to finish.
   */
  const startPolygonEdit = useCallback((existingPaths, onComplete, onCancel) => {
    const map = mapRef.current;
    if (!map || !existingPaths?.length) return;

    // Cancel any in-progress draw/edit
    if (handlerRef.current?.disable) {
      try { handlerRef.current.disable(); } catch (_) {}
      handlerRef.current = null;
    }
    setEditing(true);
    originalPathsRef.current = existingPaths;
    onCompleteRef.current = onComplete;
    onCancelRef.current = onCancel;

    const latlngs = existingPaths.map(p => [p.lat, p.lng]);
    const editLayer = L.polygon(latlngs, {
      color: '#ff9800',
      weight: 3,
      opacity: 0.9,
      fillColor: '#ff9800',
      fillOpacity: 0.1,
      dashArray: '6, 4',
    });
    editLayer.addTo(map);
    editLayer.editing.enable();
    tempLayerRef.current = editLayer;

    handlerRef.current = {
      disable: () => {
        try { editLayer.editing.disable(); } catch (_) {}
        try { map.removeLayer(editLayer); } catch (_) {}
        tempLayerRef.current = null;
      },
    };
  }, [mapRef]);

  const confirmEdit = useCallback(() => {
    const editLayer = tempLayerRef.current;
    if (!editLayer) return;
    const updatedLatLngs = editLayer.getLatLngs()[0] || [];
    const paths = updatedLatLngs.map(ll => ({ lat: ll.lat, lng: ll.lng }));
    if (handlerRef.current?.disable) handlerRef.current.disable();
    handlerRef.current = null;
    setEditing(false);
    if (paths.length >= 3 && onCompleteRef.current) onCompleteRef.current(paths);
    onCompleteRef.current = null;
    onCancelRef.current = null;
    originalPathsRef.current = null;
  }, []);

  const cancelEdit = useCallback(() => {
    if (handlerRef.current?.disable) handlerRef.current.disable();
    handlerRef.current = null;
    setEditing(false);
    if (onCancelRef.current) onCancelRef.current();
    onCompleteRef.current = null;
    onCancelRef.current = null;
    originalPathsRef.current = null;
  }, []);

  const undoEdit = useCallback(() => {
    const map = mapRef.current;
    const original = originalPathsRef.current;
    if (!map || !original?.length || !tempLayerRef.current) return;

    // Remove current edit layer, recreate from original
    if (handlerRef.current?.disable) handlerRef.current.disable();

    const latlngs = original.map(p => [p.lat, p.lng]);
    const editLayer = L.polygon(latlngs, {
      color: '#ff9800',
      weight: 3,
      opacity: 0.9,
      fillColor: '#ff9800',
      fillOpacity: 0.1,
      dashArray: '6, 4',
    });
    editLayer.addTo(map);
    editLayer.editing.enable();
    tempLayerRef.current = editLayer;

    handlerRef.current = {
      disable: () => {
        try { editLayer.editing.disable(); } catch (_) {}
        try { map.removeLayer(editLayer); } catch (_) {}
        tempLayerRef.current = null;
      },
    };
  }, [mapRef]);

  const cancelDraw = useCallback(() => {
    if (handlerRef.current?.disable) {
      try { handlerRef.current.disable(); } catch (_) {}
      handlerRef.current = null;
    }
    setEditing(false);
    onCompleteRef.current = null;
    onCancelRef.current = null;
    originalPathsRef.current = null;
  }, []);

  return {
    startPolygonDraw,
    startPolygonEdit,
    confirmEdit,
    cancelEdit,
    undoEdit,
    cancelDraw,
    editing,
  };
}
